import {
  getAll,
  getCurrent,
  getNavigation,
} from '@rover/react-lib/src/apis/countryConfiguration.api';
import EXPERIMENTS from '@rover/react-lib/src/constants/experiments.constants';
import { GOOGLE_MAP_CUSTOM_STYLE } from '@rover/react-lib/src/constants/googleMap.constants';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import { selectors as experimentsSelectors } from '@rover/react-lib/src/redux/ducks/experiments.duck';
import { CountryConfiguration, CountryConfigurationUrls } from '@rover/rsdk/src/apiClient/latest';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import { captureMessage } from '@rover/rsdk/src/modules/ErrorReporting/sentry';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import {
  DEFAULT_PRINTABLE_BOOKING_FEE_CAP,
  DEFAULT_PRINTABLE_BOOKING_FEE_PERCENTAGE,
} from '@rover/shared/js/constants/service';
import type { GoogleMapsConfigurationType } from '@rover/types/src/GoogleMapsConfiguration';
import partialRegExp from '@rover/utilities/partialRegExp';

export type State = {
  countryConfigurations: CountryConfiguration[];
  currentCountryConfiguration: CountryConfiguration | null | undefined;
  navigationCountryConfiguration: CountryConfiguration | null | undefined;
  errorMessage: string;
  isLoading: boolean;
};

type Actions = {
  fetchConfigurationPending: ActionCreator<void>;
  fetchConfigurationFailure: ActionCreator<Error>;
  fetchConfigurationSuccess: ActionCreator<{
    currentCountryConfiguration: CountryConfiguration;
    countryConfigurations: CountryConfiguration[];
    navigationCountryConfiguration: CountryConfiguration;
  }>;
};

type Effects = {
  fetchConfiguration: EffectCreator<void>;
};

type Selectors = {
  getIsLoading: SelectorCreator<boolean>;
  getErrorMessage: SelectorCreator<string | null | undefined>;
  getCurrentCountryConfiguration: SelectorCreator<CountryConfiguration | null | undefined>;
  getAvailableLanguages: SelectorCreator<Array<[string, string]> | null | undefined>;
  getCurrentPostalCodeRegex: SelectorCreator<RegExp | null | undefined>;
  getCurrentPartialPostalCodeRegex: SelectorCreator<RegExp | null | undefined>;
  getCurrentIsStateRequired: SelectorCreator<boolean | null | undefined>;
  getCurrentCustomerSupportPhoneFormatted: SelectorCreator<string | null | undefined>;
  getCurrentDogTrainingCustomerSupportPhoneFormatted: SelectorCreator<string | undefined>;
  getCurrentEmergencyHotlinePhoneFormatted: SelectorCreator<string | null | undefined>;
  getCurrentEmergencyHotlinePhoneWeb: SelectorCreator<string>;
  getAllCountryConfigurations: SelectorCreator<CountryConfiguration[]>;
  getAllFormattedRegexes: SelectorCreator<RegExp[]>;
  getCombinedRegex: SelectorCreator<RegExp>;
  getCombinedPartialRegex: SelectorCreator<RegExp>;
  getCountryConfigurationByPostalCode: SelectorCreator<
    (arg0: string) => CountryConfiguration | null | undefined
  >;
  getIsStateRequiredByPostalCode: SelectorCreator<(arg0: string) => boolean>;
  getStateFieldNameByPostalCode: SelectorCreator<(arg0: string) => string>;
  getWeightLabel: SelectorCreator<string>;
  getDogSizes: SelectorCreator<
    Array<{
      range: string;
      key: string;
    }>
  >;
  getCurrentCountryIndexUrl: SelectorCreator<string>;
  getCurrentContinentIsEurope: SelectorCreator<boolean>;
  getCurrentCountryCode: SelectorCreator<string>;
  getCurrentCurrencyCode: SelectorCreator<string>;
  getCurrentCurrencySymbol: SelectorCreator<string>;
  getCurrentCountryUrls: SelectorCreator<CountryConfigurationUrls>;
  getCurrentCountryMinimumAddOnPrices: SelectorCreator<Record<string, Record<string, number>>>;
  getCurrentCountryMaximumServiceAddOnPrices: SelectorCreator<
    Record<string, Record<string, number>>
  >;
  getCurrentCountryMaxAddOnPrice: SelectorCreator<number | null>;
  getCurrentCountryPrintableBookingFeePercentage: SelectorCreator<string>;
  getCurrentCountryPrintableBookingFeeCap: SelectorCreator<string>;
  getCountryCodes: SelectorCreator<string[]>;
  getExtenuatingCircumstancesArticleURL: SelectorCreator<string>;
  getEmergencyHotlineHours: SelectorCreator<string>;
  getCxOperatingHours: SelectorCreator<string>;
  getNavigationCountryConfiguration: SelectorCreator<CountryConfiguration | null | undefined>;
  getNavigationCountryUrls: SelectorCreator<CountryConfigurationUrls>;
  getNavigationAvailableLanguages: SelectorCreator<Array<[string, string]> | null | undefined>;
  getNavigationCurrentContinentIsEurope: SelectorCreator<boolean>;
  getNavigationCurrencyCode: SelectorCreator<string>;
  getPriceFilterMaxValue: SelectorCreator<number | null | undefined>;
  getPriceFilterMinValue: SelectorCreator<number | null | undefined>;
  getRoverGuaranteeUrl: SelectorCreator<string | undefined>;
  getGoogleMapsExperiment: SelectorCreator<boolean>;
  getGoogleMapsConfiguration: SelectorCreator<GoogleMapsConfigurationType[]>;
  getNavigationHasDogTraining: SelectorCreator<boolean>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

const buildPostalCodeRegex = (inputRegex): RegExp => {
  /**
   * Some postal code regexes come from the backend
   * without the needed leading caret (^). This ensures
   * that the regex starts with a caret.
   */
  if (!inputRegex || inputRegex.length === 0) {
    return /^/;
  }
  const regEx = inputRegex[0] === '^' ? inputRegex : `^${inputRegex}`;
  try {
    return new RegExp(regEx);
  } catch (e) {
    Logger.error(
      new Error(`Error parsing regex str: ${regEx}. Please check the PostalCodeConfiguration.`)
    );
    return /^/;
  }
};

export const options: Options = {
  name: 'countryConfiguration',
  initialState: {
    isLoading: false,
    errorMessage: '',
    currentCountryConfiguration: null,
    countryConfigurations: [],
    navigationCountryConfiguration: null,
  },
  actions: {
    fetchConfigurationPending: () => (state) => ({
      ...state,
      errorMessage: '',
      isLoading: true,
    }),
    fetchConfigurationFailure:
      ({ message }) =>
      (state) => ({
        ...state,
        errorMessage: message,
        isLoading: false,
      }),
    fetchConfigurationSuccess:
      ({ currentCountryConfiguration, countryConfigurations, navigationCountryConfiguration }) =>
      (state) => ({
        ...state,
        currentCountryConfiguration,
        countryConfigurations,
        navigationCountryConfiguration,
        errorMessage: '',
        isLoading: false,
      }),
  },
  effects: {
    fetchConfiguration:
      () =>
      async (
        dispatch,
        getState,
        { fetchConfigurationPending, fetchConfigurationSuccess, fetchConfigurationFailure }
      ) => {
        dispatch(fetchConfigurationPending());

        try {
          const [
            currentCountryConfiguration,
            countryConfigurations,
            navigationCountryConfiguration,
          ] = await Promise.all([getCurrent(), getAll(), getNavigation()]);
          dispatch(
            fetchConfigurationSuccess({
              currentCountryConfiguration,
              countryConfigurations,
              navigationCountryConfiguration,
            })
          );
        } catch (e) {
          dispatch(fetchConfigurationFailure(e as Error));
          captureError(e as Error);
        }
      },
  },
  selectors: {
    getIsLoading: (getDuckState, createSelector) =>
      createSelector([getDuckState], (state) => state.isLoading),
    getErrorMessage: (getDuckState, createSelector) =>
      createSelector([getDuckState], (state) => state.errorMessage),
    getCurrentCountryConfiguration: (getDuckState, createSelector) =>
      createSelector([getDuckState], (state) => state.currentCountryConfiguration),
    getAvailableLanguages: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector([getCurrentCountryConfiguration], (countryConfiguration) => {
        if (!countryConfiguration) return null;
        return countryConfiguration.availableLanguages;
      }),
    getCurrentPostalCodeRegex: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector([getCurrentCountryConfiguration], (countryConfiguration) => {
        if (!countryConfiguration) return null;
        if (!countryConfiguration.postalCodeRegex) {
          captureMessage(
            `Country ${countryConfiguration.countryCode} does not have a postalCodeRegex set`
          );
          return null;
        }
        return buildPostalCodeRegex(countryConfiguration.postalCodeRegex);
      }),
    getCurrentPartialPostalCodeRegex: (
      getDuckState,
      createSelector,
      { getCurrentPostalCodeRegex }
    ) => createSelector([getCurrentPostalCodeRegex], (regex) => regex && partialRegExp(regex)),
    getCurrentIsStateRequired: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector(
        [getCurrentCountryConfiguration],
        (countryConfiguration) => countryConfiguration && countryConfiguration.isStateRequired
      ),
    getCurrentCustomerSupportPhoneFormatted: (
      getDuckState,
      createSelector,
      { getCurrentCountryConfiguration }
    ) =>
      createSelector(
        [getCurrentCountryConfiguration],
        (countryConfiguration) =>
          countryConfiguration &&
          countryConfiguration.phoneNumbers &&
          countryConfiguration.phoneNumbers.customerSupportPhoneFormatted
      ),
    getCurrentDogTrainingCustomerSupportPhoneFormatted: (
      getDuckState,
      createSelector,
      { getCurrentCountryConfiguration }
    ) =>
      createSelector(
        [getCurrentCountryConfiguration],
        (countryConfiguration) =>
          countryConfiguration &&
          countryConfiguration.phoneNumbers &&
          countryConfiguration.phoneNumbers.dogTrainingCustomerSupportPhoneFormatted
      ),
    getCurrentEmergencyHotlinePhoneFormatted: (
      getDuckState,
      createSelector,
      { getCurrentCountryConfiguration }
    ) =>
      createSelector(
        [getCurrentCountryConfiguration],
        (countryConfiguration) =>
          countryConfiguration &&
          countryConfiguration.phoneNumbers &&
          countryConfiguration.phoneNumbers.emergencyHotlinePhoneFormatted
      ),
    getCurrentEmergencyHotlinePhoneWeb: (
      getDuckState,
      createSelector,
      { getCurrentCountryConfiguration }
    ) =>
      createSelector(
        [getCurrentCountryConfiguration],
        (countryConfiguration) =>
          countryConfiguration &&
          countryConfiguration.phoneNumbers &&
          countryConfiguration.phoneNumbers.emergencyHotlinePhoneWeb
      ),
    getAllCountryConfigurations: (getDuckState, createSelector) =>
      createSelector([getDuckState], (state) => state.countryConfigurations),
    getAllFormattedRegexes: (getDuckState, createSelector, { getAllCountryConfigurations }) =>
      createSelector([getAllCountryConfigurations], (countryConfigurations) =>
        countryConfigurations.map((config) => buildPostalCodeRegex(config.postalCodeRegex))
      ),
    getCombinedRegex: (getDuckState, createSelector, { getAllFormattedRegexes }) =>
      createSelector(
        [getAllFormattedRegexes],
        (regexes) => new RegExp(regexes.map((r) => r.source).join('|'))
      ),
    getCombinedPartialRegex: (getDuckState, createSelector, { getCombinedRegex }) =>
      createSelector([getCombinedRegex], (regex) => regex && partialRegExp(regex)),
    getCountryConfigurationByPostalCode: (
      getDuckState,
      createSelector,
      {
        getAllCountryConfigurations,
        getCurrentCountryConfiguration,
        getCurrentPartialPostalCodeRegex,
      }
    ) =>
      createSelector(
        [
          getAllCountryConfigurations,
          getCurrentCountryConfiguration,
          getCurrentPartialPostalCodeRegex,
        ],
        (countryConfigurations, currentCountryConfiguration, partial) => (postalCode) => {
          if (!postalCode || (partial && partial.test(postalCode))) {
            return currentCountryConfiguration;
          }

          return (
            countryConfigurations.find((config) =>
              partialRegExp(buildPostalCodeRegex(config.postalCodeRegex)).test(postalCode)
            ) || currentCountryConfiguration
          );
        }
      ),
    getIsStateRequiredByPostalCode: (
      getDuckState,
      createSelector,
      { getCountryConfigurationByPostalCode }
    ) =>
      createSelector(
        [getCountryConfigurationByPostalCode],
        (countryConfigurationByPostalCode) => (postalCode) => {
          const config = countryConfigurationByPostalCode(postalCode);
          return config ? config.isStateRequired : true;
        }
      ),
    getCurrentContinentIsEurope: (
      getDuckState,
      createSelector,
      { getCurrentCountryConfiguration }
    ) =>
      createSelector([getCurrentCountryConfiguration], (countryConfiguration) => {
        if (!countryConfiguration) return false;
        return countryConfiguration.continentCode === 'EU';
      }),
    getCurrentCountryCode: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector([getCurrentCountryConfiguration], (countryConfiguration) =>
        countryConfiguration ? countryConfiguration.countryCode : 'US'
      ),
    getCurrentCurrencyCode: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector([getCurrentCountryConfiguration], (countryConfiguration) =>
        countryConfiguration ? countryConfiguration.currencyCode : 'USD'
      ),
    getCurrentCurrencySymbol: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector([getCurrentCountryConfiguration], (countryConfiguration) =>
        countryConfiguration ? countryConfiguration.currencySymbol : '$'
      ),
    getCurrentCountryUrls: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector([getCurrentCountryConfiguration], (currentCountryConfiguration) => {
        if (!currentCountryConfiguration) return {};
        return currentCountryConfiguration.urls;
      }),
    getCurrentCountryMinimumAddOnPrices: (
      getDuckState,
      createSelector,
      { getCurrentCountryConfiguration }
    ) =>
      createSelector([getCurrentCountryConfiguration], (currentCountryConfiguration) => {
        if (!currentCountryConfiguration) return {};
        return currentCountryConfiguration.minimumServiceAddOnPrices;
      }),
    getCurrentCountryMaximumServiceAddOnPrices: (
      getDuckState,
      createSelector,
      { getCurrentCountryConfiguration }
    ) =>
      createSelector([getCurrentCountryConfiguration], (currentCountryConfiguration) => {
        if (!currentCountryConfiguration) return {};
        return currentCountryConfiguration.maximumServiceAddOnPrices;
      }),
    getCurrentCountryPrintableBookingFeePercentage: (
      getDuckState,
      createSelector,
      { getCurrentCountryConfiguration }
    ) =>
      createSelector(
        [getCurrentCountryConfiguration],
        (currentCountryConfiguration) =>
          currentCountryConfiguration?.bookingFeePercentage ||
          DEFAULT_PRINTABLE_BOOKING_FEE_PERCENTAGE
      ),
    getCurrentCountryPrintableBookingFeeCap: (
      getDuckState,
      createSelector,
      { getCurrentCountryConfiguration }
    ) =>
      createSelector(
        [getCurrentCountryConfiguration],
        (currentCountryConfiguration) =>
          currentCountryConfiguration?.bookingFeeCap || DEFAULT_PRINTABLE_BOOKING_FEE_CAP
      ),
    getCurrentCountryMaxAddOnPrice: (
      getDuckState,
      createSelector,
      { getCurrentCountryConfiguration }
    ) =>
      createSelector([getCurrentCountryConfiguration], (currentCountryConfiguration) => {
        if (!currentCountryConfiguration) return {};
        return currentCountryConfiguration.maxAddOnPrice;
      }),
    getCurrentCountryIndexUrl: (getDuckState, createSelector, { getCurrentCountryUrls }) =>
      createSelector([getCurrentCountryUrls], (currentCountryUrls) => {
        if (!Object.keys(currentCountryUrls).length) return 'http://www.rover.com/';
        return currentCountryUrls.indexUrl;
      }),
    getCountryCodes: (getDuckState, createSelector, { getAllCountryConfigurations }) =>
      createSelector([getAllCountryConfigurations], (allCountryConfigurations) =>
        allCountryConfigurations.map((countryConfiguration) => countryConfiguration.countryCode)
      ),
    getStateFieldNameByPostalCode: (
      getDuckState,
      createSelector,
      { getCountryConfigurationByPostalCode }
    ) =>
      createSelector(
        [getCountryConfigurationByPostalCode],
        (countryConfigurationByPostalCode) => (postalCode) => {
          const config = countryConfigurationByPostalCode(postalCode);
          return config && config.countryCode === 'CA' ? 'Province' : 'State';
        }
      ),
    getWeightLabel: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector([getCurrentCountryConfiguration], (countryConfiguration) => {
        return countryConfiguration ? countryConfiguration.weightUnit : 'lbs';
      }),
    getDogSizes: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector([getCurrentCountryConfiguration], (countryConfiguration) => {
        return countryConfiguration
          ? countryConfiguration.dogSizes
          : [
              {
                key: 'small',
                range: '0-15',
              },
              {
                key: 'medium',
                range: '16-40',
              },
              {
                key: 'large',
                range: '41-100',
              },
              {
                key: 'giant',
                range: '101+',
              },
            ];
      }),
    getExtenuatingCircumstancesArticleURL: (
      getDuckState,
      createSelector,
      { getCurrentCountryConfiguration }
    ) =>
      createSelector([getCurrentCountryConfiguration], (countryConfiguration) => {
        return countryConfiguration
          ? countryConfiguration.urls.extenuatingCircumstancesHelpdeskUrl
          : 'https://support.rover.com/hc/en-us/articles/360031574231';
      }),
    getEmergencyHotlineHours: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector([getCurrentCountryConfiguration], (countryConfiguration) =>
        countryConfiguration ? countryConfiguration.emergencyHotlineHours : ''
      ),
    getCxOperatingHours: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector([getCurrentCountryConfiguration], (countryConfiguration) =>
        countryConfiguration ? countryConfiguration.cxOperatingHours : ''
      ),
    // If you navigate to a new country-specific page, this still returns the previous country
    // value. It may be a cookie issue.
    // See DEV-41869
    getNavigationCountryConfiguration: (getDuckState, createSelector) =>
      createSelector([getDuckState], (state) => state.navigationCountryConfiguration),
    getNavigationAvailableLanguages: (
      getDuckState,
      createSelector,
      { getNavigationCountryConfiguration }
    ) =>
      createSelector([getNavigationCountryConfiguration], (navigationConfiguration) => {
        if (!navigationConfiguration) return null;
        return navigationConfiguration.availableLanguages;
      }),
    getNavigationCurrentContinentIsEurope: (
      getDuckState,
      createSelector,
      { getNavigationCountryConfiguration }
    ) =>
      createSelector([getNavigationCountryConfiguration], (navigationConfiguration) => {
        if (!navigationConfiguration) return false;
        return navigationConfiguration.continentCode === 'EU';
      }),
    getNavigationCurrencyCode: (
      getDuckState,
      createSelector,
      { getNavigationCountryConfiguration }
    ) =>
      createSelector([getNavigationCountryConfiguration], (countryConfiguration) =>
        countryConfiguration ? countryConfiguration.currencyCode : 'EUR'
      ),
    getNavigationCountryUrls: (
      getDuckState,
      createSelector,
      { getNavigationCountryConfiguration }
    ) =>
      createSelector([getNavigationCountryConfiguration], (navigationCountryConfiguration) => {
        if (!navigationCountryConfiguration) return {};
        return navigationCountryConfiguration.urls;
      }),
    getPriceFilterMaxValue: (getDuckState, createSelector, { getNavigationCountryConfiguration }) =>
      createSelector(
        [getNavigationCountryConfiguration],
        (countryConfiguration) => countryConfiguration && countryConfiguration.priceFilterMaxValue
      ),
    getPriceFilterMinValue: (getDuckState, createSelector, { getNavigationCountryConfiguration }) =>
      createSelector(
        [getNavigationCountryConfiguration],
        (countryConfiguration) => countryConfiguration && countryConfiguration.priceFilterMinValue
      ),
    getRoverGuaranteeUrl: (getDuckState, createSelector, { getNavigationCountryConfiguration }) =>
      createSelector(
        [getNavigationCountryConfiguration],
        (countryConfiguration) =>
          (countryConfiguration && countryConfiguration.roverGuaranteeUrl) || undefined
      ),
    getGoogleMapsExperiment: (getState, createSelector) =>
      createSelector([experimentsSelectors.isVariant], (isVariant) =>
        isVariant(EXPERIMENTS.INTERNATIONA_SEARCH_CONTEXT_CLUES_EXPERIMENT)
      ),
    getGoogleMapsConfiguration: (
      getDuckState,
      createSelector,
      { getNavigationCountryConfiguration, getGoogleMapsExperiment }
    ) =>
      createSelector(
        [getNavigationCountryConfiguration, getGoogleMapsExperiment],
        (countryConfiguration, isInVariantGoogleMapsExperiment) => {
          if (
            !isInVariantGoogleMapsExperiment ||
            !countryConfiguration ||
            !countryConfiguration.googleMapsConfiguration
          )
            return GOOGLE_MAP_CUSTOM_STYLE;
          return countryConfiguration.googleMapsConfiguration;
        }
      ),
    getNavigationHasDogTraining: (
      getDuckState,
      createSelector,
      { getNavigationCountryConfiguration }
    ) =>
      createSelector([getNavigationCountryConfiguration], (navigationConfiguration) => {
        return navigationConfiguration?.hasDogTraining ?? false;
      }),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
